import { createSlice } from '@reduxjs/toolkit'
import { requests } from '../../../core/ajax'
import { feedback } from '../../../core/feedback'

const mailingConfigSlice = createSlice({
  name: 'mailingConfig',
  initialState: {
    isLoading: false,
    categories: [],
    availableCategories: [],
    error: null,
  },
  reducers: {
    setLoading: (state, { payload }) => {
      state.isLoading = payload
      if (payload) {
        state.error = null
      }
    },
    setApplicationsData: (state, { payload }) => {
      state.categories = payload
      state.isLoading = false
      state.error = null
    },
    updateApplicationStatus: (state, { payload }) => {
      const { categoryId, applicationId, status } = payload
      const category = state.categories.find((cat) => cat.id === categoryId)
      if (category) {
        const application = category.applications.find((app) => app.id === applicationId)
        if (application) {
          if (application.required && status === false) {
            return
          }
          application.status = status
        }
      }
    },
    toggleCategoryStatus: (state, { payload }) => {
      const { categoryId, status } = payload
      const category = state.categories.find((cat) => cat.id === categoryId)
      if (category) {
        category.applications.forEach((app) => {
          if (!app.required) {
            app.status = status
          }
        })
      }
    },
    setError: (state, { payload }) => {
      state.error = payload
      state.isLoading = false
    },
    setAvailableCategories: (state, { payload }) => {
      state.availableCategories = payload
    },
  },
})

export const mailingConfigReducer = mailingConfigSlice.reducer
export const {
  setLoading,
  setApplicationsData,
  updateApplicationStatus,
  toggleCategoryStatus,
  setError,
  setAvailableCategories,
} = mailingConfigSlice.actions

export const getAvailableCategories = () => (dispatch) => {
  const { get } = requests

  get('/combo_box', { params: { parent_id: 'categorias_email' } })
    .then(({ data }) => {
      dispatch(setAvailableCategories(data))
    })
    .catch(({ response }) => {
      console.error('Error recuperando categorías disponibles', response)
    })
}

export const getMailingConfig = () => (dispatch, getState) => {
  const { get } = requests

  dispatch(setLoading(true))

  const categoriesPromise = get('/combo_box', { params: { parent_id: 'categorias_email' } })
  const emailsPromise = get('/preferencias_email/emails')

  Promise.all([categoriesPromise, emailsPromise])
    .then(([categoriesResponse, emailsResponse]) => {
      const availableCategories = categoriesResponse.data
      const emails = emailsResponse.data

      dispatch(setAvailableCategories(availableCategories))

      const groupedByCategory = {}

      availableCategories.forEach((category) => {
        groupedByCategory[category.value] = {
          id: category.value,
          name: category.label || category.value,
          applications: [],
        }
      })

      emails.forEach((email) => {
        if (!email.id || email.id.trim() === '') {
          console.warn('Email omitido por ID vacío:', email)
          return
        }

        const categoryName = email.category
        if (!groupedByCategory[categoryName]) {
          groupedByCategory[categoryName] = {
            id: categoryName,
            name: categoryName,
            applications: [],
          }
        }

        const application = {
          id: email.id,
          name: email.email_name,
          description: email.email_description,
          required: email.necessary,
          status: email.necessary ? true : email.send,
        }

        groupedByCategory[categoryName].applications.push(application)
      })

      const categories = Object.values(groupedByCategory).filter(
        (category) => category.applications.length > 0
      )

      dispatch(setApplicationsData(categories))
    })
    .catch(({ response }) => {
      console.error('Error recuperando configuración de mailing', response)
      dispatch(setError('Error al cargar la configuración de notificaciones'))
      feedback('error', 'Error al cargar la configuración de notificaciones')
    })
}

export const toggleApplicationStatus =
  (categoryId, applicationId, newStatus) => (dispatch, getState) => {
    const { put } = requests

    if (!applicationId || applicationId.trim() === '') {
      console.error('ID de aplicación vacío, no se puede actualizar')
      feedback('error', 'Error: ID de aplicación inválido')
      return
    }

    const { categories } = getState().mailingConfig
    const category = categories.find((cat) => cat.id === categoryId)
    if (category) {
      const application = category.applications.find((app) => app.id === applicationId)
      if (application && application.required && newStatus === false) {
        feedback('warning', 'No se pueden desactivar las notificaciones obligatorias')
        return
      }
    }

    put(`/preferencias_email/emails/${applicationId}?send=${newStatus}`)
      .then(({ data }) => {
        dispatch(updateApplicationStatus({ categoryId, applicationId, status: newStatus }))
        feedback('success', 'Configuración actualizada correctamente')
      })
      .catch(({ response }) => {
        console.error('Error actualizando configuración', response)
        const errorMessage = response?.data?.message || 'Error al actualizar la configuración'
        feedback('error', errorMessage)
      })
  }

export const toggleCategoryAllStatus = (categoryId, newStatus) => (dispatch, getState) => {
  const { put } = requests
  const { categories } = getState().mailingConfig

  const category = categories.find((cat) => cat.id === categoryId)
  if (!category) return

  const applicationsToUpdate = category.applications.filter(
    (app) => !app.required && app.id && app.id.trim() !== ''
  )

  if (applicationsToUpdate.length === 0) {
    feedback('info', 'No hay elementos modificables en esta categoría')
    return
  }

  const updatePromises = applicationsToUpdate.map((app) =>
    put(`/preferencias_email/emails/${app.id}?send=${newStatus}`)
  )

  Promise.all(updatePromises)
    .then(() => {
      dispatch(toggleCategoryStatus({ categoryId, status: newStatus }))
      feedback('success', `Categoría ${newStatus ? 'activada' : 'desactivada'} correctamente`)
    })
    .catch(({ response }) => {
      console.error('Error actualizando categoría', response)
      const errorMessage = response?.data?.message || 'Error al actualizar la categoría'
      feedback('error', errorMessage)
    })
}
